"use client";

import {
  getDistanceFromLatLonInKm,
  getDistanceText,
} from '@wt/app/game/_utils/getGameScore';
import useSettingsContext from '@wt/game/providers/settings/types';
import MapContext from '@wt/shared/libs/mapkit/context/MapContext';
import { getSiteThemes } from '@wt/utilities/siteMode';
import { useContext, useEffect } from 'react';

export const MapDistancePoints = ({
  froms,
  to,
}: {
  froms: number[][];
  to: number[];
}) => {
  const [settings] = useSettingsContext();
  const map = useContext(MapContext);
  const polylines: mapkit.PolylineOverlay[] = [];
  let annotations: mapkit.MarkerAnnotation[] = [];
  let showAnnotations: mapkit.MarkerAnnotation[] = [];

  useEffect(() => {
    if (map === null) return undefined;

    if (map) {
      if (polylines && polylines.length > 0) {
        map.removeOverlays(polylines);
      }
      if (annotations && annotations.length > 0) {
        map.removeAnnotations(annotations);
      }

      // polyline is drawn differently depending on longitude given
      // https://developer.apple.com/documentation/mapkitjs/polylineoverlay
      // so we might need to adjust coordinates to ensure shortest line is drawn

      const trueLocation = new mapkit.Coordinate(Number(to[0]), Number(to[1]));
      const trueLocationAnnotation = new mapkit.MarkerAnnotation(trueLocation);
      trueLocationAnnotation.color = '#000000';
      trueLocationAnnotation.title = 'Location';
      const style = new mapkit.Style({
        lineWidth: 1.5,
        lineJoin: 'round',
        lineDash: [8, 6],
        strokeColor: '#000000',
      });

      showAnnotations = [trueLocationAnnotation];
      annotations = [trueLocationAnnotation];
      map.addAnnotation(trueLocationAnnotation);

      let closestGuessAnnotation: mapkit.MarkerAnnotation | null = null;
      let closestGuessDistance = 0;
      for (let i = 0; i < froms.length; i++) {
        const from = froms[i];
        const shortestPathCoords: number[][] = getShortestPathCoordinates(
          from,
          to
        );

        const coords = shortestPathCoords.map(function (point: number[]) {
          return new mapkit.Coordinate(point[0], point[1]);
        });

        const distance = Math.round(
          getDistanceFromLatLonInKm(
            Number(from[0]),
            Number(from[1]),
            to[0],
            to[1]
          )
        );

        const guess = new mapkit.Coordinate(Number(from[0]), Number(from[1]));
        const guessAnnotation = new mapkit.MarkerAnnotation(guess);
        if (i == 1) {
          guessAnnotation.color = getSiteThemes().darkTheme.error;
        } else {
          guessAnnotation.color = getSiteThemes().darkTheme.accent;
        }
        guessAnnotation.title = 'Your guess';
        guessAnnotation.subtitle = getDistanceText(
          1000 * distance,
          settings?.distanceUnit
        );
        guessAnnotation.subtitleVisibility = mapkit.FeatureVisibility.Visible;

        if (!closestGuessAnnotation || distance < closestGuessDistance) {
          closestGuessAnnotation = guessAnnotation;
          closestGuessDistance = distance;
        }

        const polyline = new mapkit.PolylineOverlay(coords, { style });

        polylines.push(polyline);
        annotations.push(guessAnnotation);

        map.addAnnotation(guessAnnotation);
        map.addOverlay(polyline);
      }
      if (closestGuessAnnotation) showAnnotations.push(closestGuessAnnotation);

      setTimeout(() => {
        map.showItems(showAnnotations, {
          animate: true,
        });
      }, 700);
    }
  }, [froms, to, map]);

  return null;
};

const normalizeLongitude = (lon: number): number => {
  // normalize longitude to be within the range -180 to 180
  return ((lon + 180) % 360 + 360) % 360 - 180;
}

const getShortestPathCoordinates = (coord1: number[], coord2: number[]): number[][] => {
  // get the longitude difference between the two points
  const lonDiff = coord2[1] - coord1[1];

  // normalize the longitude difference to be within a 360-degree range
  const normalizedLonDiff = normalizeLongitude(lonDiff);

  // adjust the second coordinate's longitude to ensure the shortest path
  const newLon2 = coord1[1] + normalizedLonDiff;

  // return the adjusted coordinates
  return [coord1, [coord2[0], newLon2]];
}
